import React, { useState, useEffect, useMemo } from 'react';
import ButtonComponent from '../shared/button/button.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../routes/route.path';
import { SecureDocsService } from '../../services/SecureDocsService';
import { ProfileModel } from '../../models/profile.model';
import { UserService } from '../../services/UserService';
import { Document, Page, pdfjs } from 'react-pdf';
import ConfiguredFieldDisplay from './configured-fields-display.component';
import { useSelector } from 'react-redux';
import * as selector from "../../selectors/selectors";

interface Props {

}

const DisplaySuccessMaessageComponent: React.FC<Props> = (props) => {
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const location = useLocation();
    const params = new URLSearchParams(location.search)
    const fileId = params.get("fileId")

    const [pdfData, setPdfData] = useState<Uint8Array | null>(null)
    const [agreementData, setAgreementData] = useState<any>(null);
    const [numPages, setNumPages] = useState<number>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const fileData = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const Profile: ProfileModel = UserService.getCurrentUserFromLocalStorage();

    useEffect(() => {
        const fetchData = () => {
            SecureDocsService.getFileConfigurations(fileId)
                .then((res) => {
                    const pdfBase64 = res.data.file;
                    const byteCharacters = atob(pdfBase64);
                    const byteArray = Uint8Array.from(byteCharacters, (c) => c.charCodeAt(0));
                    const config = res.data.configurations;
                    
                    const recipientEmails = config.elements.map(element => 
                        element.assigned_recipient.email
                    );
                    
                    const uniqueRecipientEmails = Array.from(new Set(recipientEmails));
                    
                    const agreementData = {
                        name: 'This document',
                        message: `Agreement has been ${config.file_status === 0 ? 'sent' : 'completed'} for signature.`,
                        ownmail: Profile?.Email || '', 
                        copyEmails: uniqueRecipientEmails, 
                        recipients: uniqueRecipientEmails,
                        deadline: new Date(config.updated_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                        }),
                        autoDeleteDays: 100,
                        completionStatus: config.elements.every(el => el.is_completed) ? 'completed' : 'pending',
                        elements: config.elements.map(element => ({
                            id: element.element_id,
                            type: element.element_type,
                            page: element.placeholder.page,
                            position: {
                                x: Number(element.placeholder.x),
                                y: Number(element.placeholder.y),
                                width: Number(element.placeholder.width),
                                height: Number(element.placeholder.height)
                            },
                            assignedTo: element.assigned_recipient,
                            completed: element.is_completed,
                            completedAt: element.completed_at
                        }))
                    };
                    
                    setPdfData(byteArray);
                    setAgreementData(agreementData);
                })
                .catch((error) => {
                })
                .finally(() => {
                    setLoading(false);
                });
        };
        fetchData();
        
    }, [fileId]);


    if (loading) {
        return (
        <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="success-page-text pt-2 pl-3 color-primary-accent h4-semibold">Loading document summary...</p>
        </div>
        );
    }

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
        setNumPages(numPages);
    };

    const handleSubmit = async () => {
        navigate(`${AppRoutes.FALKON_DOCS}`);
    };

    const renderAllPages = () => {
        if (!numPages) return null;
        
        const pages = [];
        for (let i = 1; i <= numPages; i++) {
            const pageFields = agreementData?.elements.filter(field => field.page === i) || [];
            
            pages.push(
                <div key={i} className="pdf-page-container p-relative">
                    <div className='p-relative d-inline-block'>
                        <Page 
                            pageNumber={i} 
                            width={800}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                        />

                        {pageFields.map((field) => (
                            <ConfiguredFieldDisplay
                                key={field.id}
                                field={field}
                                currentPage={i}
                            />
                        ))}
                    </div>
                    
                    <div className='d-flex justify-between align-center' style={{ 
                        marginTop: '10px',
                        padding: '5px 10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px'
                    }}>
                        <p className='success-page-text page-number m-0'>
                            Page {i} of {numPages}
                        </p>
                    </div>
                </div>
            );
        }
        return pages;
    };

    return (
        <div className={`success-page-container bg-color-secondary ${IsMobileDevice ? 'mobile' : ''}`}>
            <div className="left-panel">
                <div  className={`pdf-container ${IsMobileDevice ? 'p-0' : ''}`}>
                    {pdfData ? (
                        <div className="pdf-viewer h-v-85 o-y-scroll">
                            <div>
                                <Document file={fileData} onLoadSuccess={onDocumentLoadSuccess}>
                                    {renderAllPages()}
                                </Document>
                            </div>
                        </div>
                    ) : (
                        <div className="pdf-placeholder">
                            <p className="success-page-text">PDF not available</p>
                        </div>
                    )}
                </div>
            </div>
            <div className="br-2 p-4 d-flex flex-direction o-hidden">
                <div className="bg-color-secondary-main color-header-title-font p-2 o-y-auto o-x-hidden flex-1">
                    <div className="d-flex align-center">
                        <div className="h2-semibold color-primary-accent opacity-4">{agreementData?.name} has been successfully sent for signature.</div>
                    </div>
                    <div className="mb-1 color-primary h6-medium">
                        <p className='h5-medium'>
                            A copy has also been sent to{' '}
                            <a href={`mailto:${agreementData?.ownmail}`}>
                                {agreementData?.ownmail}
                            </a>{' '}
                            for your records.
                        </p>
                        <p className="mt-2 h5-medium">
                            It was also sent for the following recipients in order:
                        </p>
                    </div>
                    <div className="recipient-list">
                        <h4 className="h5-medium">Recipients:</h4>
                        <div className="space-y-2">
                            {agreementData?.recipients?.map((email: string, index: number) => (
                                <div key={index} className="recipient-item d-flex align-center bg-color-primary-inactive  h5-medium mt-1">
                                    <div className="recipient-index d-flex align-center justify-center  bg-color-primary-divider color-primary-accent">{index + 1}</div>
                                    <span className="recipient-email color-primary-accent">{email}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="h5-medium">
                        <p>
                            As soon as the agreement is complete, all eligible parties will be sent copies.
                        </p>
                        <p> Reminders send until completion every Week.</p>

                        <div className="section-divider pt-1 mt-1  h5-medium color-primary">
                            <p className=" mb-2">You will be alerted:</p>
                            <p>
                                If the document you sent has not been viewed or signed by{' '}
                                <span>{agreementData?.deadline}</span>
                            </p>
                        </div>

                        <div className="alert-box h5-medium bg-color-primary-divider color-primary-accent">
                            All agreements that are not completed within {agreementData?.autoDeleteDays} days will be automatically deleted.
                        </div>

                        <div className={`d-flex justify-center align-center ${IsMobileDevice ? 'pb-4' : ''}`}>
                            <ButtonComponent
                                OnClick={handleSubmit}
                                ButtonLabel={"Go to the list of documents"}
                                ClassName={"send-sidebar-button py-1"}
                                Variant={"primary-btn-small"}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DisplaySuccessMaessageComponent;